import React from 'react'
import {cn} from './cn'
import {BENEFIT_PROVIDER_LOGO_SOURCES} from './benefitProviderLogos'

export const BENEFIT_PROVIDERS = ['multisport', 'medicover_sport', 'pzu_sport', 'fitprofit', 'fitsport'] as const

export type BenefitProvider = (typeof BENEFIT_PROVIDERS)[number]

export const BENEFIT_PROVIDER_NAMES: Record<BenefitProvider, string> = {
    multisport: 'MultiSport',
    medicover_sport: 'Medicover Sport',
    pzu_sport: 'PZU Sport',
    fitprofit: 'FitProfit',
    fitsport: 'FitSport'
}

export const parseBenefitProvider = (value: unknown): BenefitProvider | null =>
    BENEFIT_PROVIDERS.includes(value as BenefitProvider) ? (value as BenefitProvider) : null

/** Fixed card variants per brand (code = stable wire value). Mirrors the Java BenefitVariant catalogue. */
export const BENEFIT_VARIANTS: Record<BenefitProvider, readonly {code: string, name: string}[]> = {
    multisport: [
        {code: 'plus', name: 'Plus'},
        {code: 'classic', name: 'Classic'},
        {code: 'light', name: 'Light'},
        {code: 'student', name: 'Student'},
        {code: 'senior', name: 'Senior'}
    ],
    medicover_sport: [
        {code: 'gofit', name: 'goFIT'},
        {code: 'fit_and_more', name: 'FIT&more'}
    ],
    pzu_sport: [
        {code: 'na_start', name: 'Na Start'},
        {code: 'active', name: 'Active'},
        {code: 'premium', name: 'Premium'}
    ],
    fitprofit: [
        {code: 'standard', name: 'FitProfit'},
        {code: 'junior', name: 'Junior'},
        {code: 'senior', name: 'Senior'}
    ],
    fitsport: []
}

/** "{Brand} {Variant}"; a variant named like its brand (FitProfit standard) collapses to the brand. */
export const benefitCardName = (provider: BenefitProvider, variant?: string | null): string => {
    const brand = BENEFIT_PROVIDER_NAMES[provider]
    const name = BENEFIT_VARIANTS[provider].find(entry => entry.code === variant)?.name
    return !name || name === brand ? brand : `${brand} ${name}`
}

export interface BenefitCardRef {
    provider: BenefitProvider
    variant?: string | null
}

/** Selection id of a card: `provider` or `provider:variant`. */
export const benefitCardKey = ({provider, variant}: BenefitCardRef): string =>
    variant ? `${provider}:${variant}` : provider

export const parseBenefitCardKey = (key: string): BenefitCardRef | null => {
    const [provider, variant] = key.split(':')
    const parsed = parseBenefitProvider(provider)
    return parsed ? {provider: parsed, variant: variant || null} : null
}

export interface BenefitCardScope {
    brands: {provider: BenefitProvider, variants: readonly string[]}[]
}

export interface BenefitSummaryLabels {
    all: string
    allExcept: (brands: string) => string
    without: (brand: string, variants: string) => string
}

/**
 * One-line summary of a promotion's card scope: "All benefit cards", "All except FitSport",
 * or the brands, a restricted brand by its accepted variants: "MultiSport Plus, Classic". Empty variants = every variant of the brand.
 */
export const summarizeBenefitCardScope = (scope: BenefitCardScope, labels: BenefitSummaryLabels): string => {
    const brands = BENEFIT_PROVIDERS.flatMap(provider => scope.brands.filter(b => b.provider === provider))
    const excluded = BENEFIT_PROVIDERS.filter(provider => !brands.some(b => b.provider === provider))
    if (excluded.length === 0 && brands.every(b => b.variants.length === 0)) return labels.all
    if (excluded.length > 0 && excluded.length < brands.length && brands.every(b => b.variants.length === 0)) {
        return labels.allExcept(excluded.map(provider => BENEFIT_PROVIDER_NAMES[provider]).join(', '))
    }
    return brands.map(({provider, variants}) => {
        const all = BENEFIT_VARIANTS[provider]
        const accepted = all.filter(v => variants.includes(v.code))
        if (accepted.length === 0 || accepted.length === all.length) return BENEFIT_PROVIDER_NAMES[provider]
        const brandName = BENEFIT_PROVIDER_NAMES[provider]
        if (accepted.some(v => v.name === brandName)) {
            // The brand-named variant alone would read as the whole brand, so say what is left out.
            if (accepted.length === 1) {
                return labels.without(brandName, all.filter(v => !accepted.includes(v)).map(v => v.name).join(', '))
            }
            return accepted.map(v => benefitCardName(provider, v.code)).join(', ')
        }
        return `${brandName} ${accepted.map(v => v.name).join(', ')}`
    }).join(', ')
}

const LOGOS: Partial<Record<BenefitProvider, {src: string, background: string, showName?: boolean}>> = {
    medicover_sport: {src: BENEFIT_PROVIDER_LOGO_SOURCES.medicover_sport, background: '#ffffff'},
    multisport: {src: BENEFIT_PROVIDER_LOGO_SOURCES.multisport, background: '#00094e'},
    // The published PZU mark is an emblem without a wordmark.
    pzu_sport: {src: BENEFIT_PROVIDER_LOGO_SOURCES.pzu_sport, background: '#ffffff', showName: true}
}

/** True when the logo itself carries the brand name, so it can stand next to a text that names the card. */
export const hasBenefitProviderWordmark = (provider: BenefitProvider): boolean =>
    Boolean(LOGOS[provider]) && !LOGOS[provider]?.showName

interface BenefitProviderLogoProps {
    provider: BenefitProvider
    className?: string
    /** Inside another frame (a badge): no ring of its own and a tighter plate, so the logo itself is larger. */
    plain?: boolean
}

/** Benefit card brand: the provider's logo, or its name when no official logo is published. */
export const BenefitProviderLogo: React.FC<BenefitProviderLogoProps> = ({provider, className, plain = false}) => {
    const name = BENEFIT_PROVIDER_NAMES[provider]
    const logo = LOGOS[provider]
    if (!logo) {
        if (plain) return <span className={cn('font-bold', className)}>{name}</span>
        return (
            <span className={cn('inline-flex h-7 items-center rounded-md bg-white dark:bg-gray-900 px-2 text-[13px] font-bold text-gray-900 dark:text-gray-100 ring-1 ring-gray-200 dark:ring-gray-700', className)}>
                {name}
            </span>
        )
    }
    return (
        <span
            className={cn(plain ? 'inline-flex h-5 items-center gap-1 rounded px-1 py-px' : 'inline-flex h-7 items-center gap-1.5 rounded-md px-2 py-1.5 ring-1 ring-gray-200 dark:ring-white/10', className)}
            style={{background: logo.background}}
        >
            {/* eslint-disable-next-line @next/next/no-img-element -- inline data URI, nothing to optimize */}
            <img src={logo.src} alt={logo.showName ? '' : name} title={name} className="h-full w-auto" />
            {logo.showName ? <span className="text-[13px] font-bold text-gray-900">{name}</span> : null}
        </span>
    )
}

/**
 * The card a benefit pass requires, set off as a badge under its name and price: "Wymaga" with the
 * brand logo, or "Wymaga karty benefit" when the pass names no brand.
 */
export function BenefitCardRequirement({provider, label, genericLabel, icon, className}: {
    provider?: string | null
    label: string
    genericLabel: string
    icon?: React.ReactNode
    className?: string
}) {
    const brand = parseBenefitProvider(provider)
    return (
        <p className={cn('inline-flex w-fit max-w-full flex-wrap items-center gap-1.5 rounded-lg border border-primary-200 bg-white/85 px-2 py-1 text-sm font-semibold leading-5 text-primary-800 dark:border-primary-800 dark:bg-gray-900/70 dark:text-primary-200', className)}>
            {icon}
            {brand ? <><span>{label}</span><BenefitProviderLogo provider={brand} plain /></> : <span>{genericLabel}</span>}
        </p>
    )
}
